import { sumDecimalStrings } from '@/lib/decimal';
import { formatDecimalString } from '@/lib/format';
import type { CompanyMatrixLine, MatrixCycle } from '@/lib/revenue-matrix';
import { cycleContaining, todayIso } from '@/lib/use-revenue-analytics';
import type {
  RevenueProductCycle,
  RevenueProductRef,
  RevenueProductRow,
  RevenueProductTotalCell,
  RevenueTaxMeasure,
} from '@/lib/types';

/**
 * THE ARITHMETIC AND THE VOCABULARY BEHIND THE **Revenue by Product** TAB.
 *
 * ⚠️ **A `.ts`, NOT A `.tsx`, AND THAT IS THE LINT RULE RATHER THAN TASTE.**
 * `react(only-export-components)` warns on any file exporting both a component
 * and something else, so every constant, type and pure function this tab shares
 * lives here and the `.tsx` files beside it export components and nothing else.
 * Same split `revenue-company-shared.ts` makes.
 *
 * ── 🔴 WHAT EVERY FIGURE ON THIS TAB IS, AND WHAT IT IS NOT ────────────────
 *
 * **Paid invoices only, and INCLUDING TAX** (owner decision 2026-10-16: cash
 * collected incl. tax, on every figure on this tab). ⚠️ *SUPERSEDED 2026-10-16:
 * "EX-TAX. The server sums `line_subtotal`; VAT is collected on the government's
 * behalf rather than earned."* The tab now sends `measure=inclTax` and labels
 * from the `meta.measure` echo (see {@link measureOfResponse}), so a server that
 * ignores the key and answers ex-tax is labelled ex-tax. A cycle is the
 * invoice's BILL DATE falling inside the cycle window. Never worded "invoiced"
 * or "billed" — an invoice nobody has paid is in none of these numbers.
 *
 * ── 🔴 THE CURRENCY CONVERTS. IT IS A BASE, NOT A FILTER — owner, 2026-09-14 ─
 *
 * ⚠️ *SUPERSEDED: "**ONE CURRENCY AT A TIME. IT IS THE DESIGN, NOT A FILTER.**
 * There is no exchange rate anywhere in this system, so two currencies are never
 * added … a product sold in two currencies appears under both, with a different
 * figure under each, and neither is "what that product earns"."* **There is an
 * exchange rate** (`f_exchange_rates`, migration 178), the server prices each
 * paid line at **its own invoice's `bill_date`**, and the figures on this tab are
 * every currency summed into one base. **The ability to filter to a single
 * currency has gone with it** — that was the trade, and it was the owner's.
 *
 * ⚠️ *SUPERSEDED 2026-09-18, the scope: "⛔ **THIS TAB ONLY.** Revenue by Company
 * and the Google Sheet mirror still FILTER."* **Revenue by Company and Top Movers
 * convert too now**, through the same `measuredInCurrency` echo on
 * `GET /revenue-tracker`. ⛔ **The Google Sheet mirrors still FILTER** and are the
 * backend's own surfaces. `src/lib/targets.ts` records which surfaces may carry
 * the conversion disclaimer, and its list is now empty — the gate is the echo.
 *
 * 🔴 **{@link amountIn} IS STILL THE ONE GATE AND ITS TEST HAS NOT CHANGED —
 * only what the answer MEANS has.** A figure enters a total when its own
 * `totalCurrency` matches the unit the figures are in. Unconverted that unit is
 * the currency the reader picked and the gate is a filter; converted it is the
 * base the SERVER echoed and the gate is a check that conversion happened.
 * ⛔ **The unit to pass it is `meta.measuredInCurrency` where that came back,
 * and the chosen currency only where it did not** — never the string that was
 * sent, whose key is `baseCurrency` and is deliberately spelled otherwise. An
 * API predating the key ignores it and answers an ordinary `200` of unconverted
 * rows, so a client comparing against its own request state would admit USD
 * money to a GBP total. **That is the single way this change can go wrong, and
 * it goes wrong silently.**
 *
 * Two cases survive the reversal unchanged:
 *
 * 1. **A figure in an UNSTATED unit is withheld from every total.**
 *    `{total: "9.00", totalCurrency: null}` is reachable, because
 *    `d_invoices.currency` is nullable — and a line with no currency cannot be
 *    converted either, so this outlives the filter it was written for.
 *    Labelling it with the base would invent a fact about money, so it is
 *    counted by {@link unstatedUnitRowCount} and stated on screen instead.
 * 2. **Two figures in ONE unit are added, and that is all summing ever did.**
 *    Converted, every cell that passes the gate is in the base, so the sums are
 *    the same code doing the same thing over a wider population.
 *
 * ⚠️ **WHAT THE ROW COUNT DOES ON A SUCCESSFUL CONVERSION: it FALLS** — 75 rows
 * to 64 on the measured sample — because currency leaves the row grain and the
 * two rows for one product collapse into one. **A shorter list is what this
 * working looks like and also what a filter narrowing too hard looks like**, and
 * nothing errors either way. Nothing in this module groups on the row `key` or
 * on `row.currency`: {@link productKeyOf} keys on the product alone, so the
 * collapse cannot collide two React keys and cannot split one product in two.
 *
 * ── ⚠️ MONEY IS AN EXACT DECIMAL STRING, END TO END ────────────────────────
 *
 * Every amount stays the string it arrived as. `Number()` appears here only to
 * ORDER a list and to produce a RATIO — never
 * to produce a figure anybody reads. `Number("18000.10") * 100` is not
 * `1801000`, and a total built that way disagrees with the database by a cent,
 * which is the disagreement Xero surfaces.
 */

/* -------------------------------------------------------------------------
 * The filter vocabulary
 * --------------------------------------------------------------------- */

/**
 * A filter's "everything" value.
 *
 * The empty string, so a default is spelled by its URL key being ABSENT — one
 * canonical address per view. Same convention as the sibling tabs.
 */
export const ALL = '';

/**
 * The band key for a row that resolves no billing region.
 *
 * ⚠️ **THE PARENTHESES ARE LOAD-BEARING.** This value sits in the URL beside
 * real slugs, and a real slug is the server's `billing_region` ENUM member —
 * lower-case letters and underscores. No ENUM member can contain a bracket, so
 * this sentinel can never be mistaken for one and can never be sent to the
 * server by accident. Do not "tidy" it to `none`, which a future region could
 * legitimately be called.
 */
export const NO_REGION_KEY = '(none)';

/** What that band is called wherever it is named. Written once so it cannot drift. */
export const NO_REGION_LABEL = 'No billing region recorded';

/**
 * ⚠️ **MATCHED ON `slug`, NEVER ON `name`.** The name is editable wording; the
 * slug is the server's own join key and is write-on-create-only. Matching the
 * name means a region an administrator renames silently stops matching a filter
 * somebody saved a link to.
 */
export function regionKeyOf(row: RevenueProductRow): string {
  return row.billingRegion?.slug ?? NO_REGION_KEY;
}

/** What the reader calls a row's band. */
export function regionLabelOf(row: RevenueProductRow): string {
  return row.billingRegion?.name ?? NO_REGION_LABEL;
}

/** One option in the billing-region picker. */
export interface RegionOption {
  value: string;
  label: string;
}

/**
 * Every band present in the data, alphabetically, with the no-band group last.
 *
 * ⚠️ **BUILT FROM THE ROWS, so the picker cannot offer a band that matches
 * nothing** — a filter that reliably returns an empty page reads as a broken
 * report rather than as a question nobody should have been asked.
 */
export function regionOptionsOf(rows: readonly RevenueProductRow[]): RegionOption[] {
  const seen = new Map<string, string>();
  for (const row of rows) seen.set(regionKeyOf(row), regionLabelOf(row));

  return [...seen]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => {
      if (a.value === NO_REGION_KEY) return 1;
      if (b.value === NO_REGION_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
}

/* -------------------------------------------------------------------------
 * 🔴 The one unit gate
 * --------------------------------------------------------------------- */

/**
 * A cell's figure **only when the cell says it is in this unit**.
 *
 * 🔴 **THE SINGLE GATE EVERY FIGURE ON THIS TAB PASSES THROUGH — the ranking,
 * the shares, the cycle strip, the focus breakdown and the over-count sentence
 * all reach the wire through here.** That is why conversion was a change to
 * THIS FUNCTION'S MEANING and to nothing downstream: no consumer knows or needs
 * to know whether a rate was applied.
 *
 * 🔴 **`currency` IS THE UNIT THESE FIGURES ARE IN, NOT THE ONE THE READER
 * ASKED FOR.** ⚠️ *SUPERSEDED 2026-09-14, the parameter's meaning and not its
 * type: "the cell says it is in this currency", where `currency` was the
 * picker's value and this gate was a FILTER hiding other currencies.* Converted,
 * the server has already summed every currency into one base and the caller
 * passes **`meta.measuredInCurrency` — the echo** — so this stops hiding money and
 * becomes the check that conversion actually happened. Unconverted, the caller
 * passes the picker's value and the old filtering behaviour returns exactly.
 *
 * ⛔ **NEVER PASS THE STRING THAT WAS SENT TO THE SERVER.** A server predating
 * `baseCurrency` ignores it and answers `200` with unconverted rows; comparing
 * against the request would admit USD cells to a GBP total, silently, on the one
 * tab whose figures a finance reader adds up. The caller resolves the unit once
 * — see `RevenueByProductPanel`'s `figuresCurrency`.
 *
 * 🔴 **AND THE REASON THE GATE EXISTS AT ALL SURVIVES CONVERSION: THE UNSTATED
 * UNIT.** `{total: "9.00", totalCurrency: null}` is reachable — `d_invoices.currency`
 * is nullable — and the obvious reading of such a cell is *"a figure, therefore
 * the unit I am looking at"*. That is money relabelled by assumption, and a line
 * with no currency on it cannot be converted either, so it is exactly as
 * unadmittable now as it was before. Gating on the cell's own `totalCurrency`
 * makes the mistake unreachable rather than guarded against: an unstated figure
 * enters no sum, no ranking and no share, anywhere.
 *
 * ⚠️ **It also means `row.currency` decides nothing.** The row's group key and
 * its own totals cannot disagree about the unit without the figure simply being
 * withheld, which is the safe direction.
 *
 * ⚠️ **`null` OUT IS "NOTHING IN THIS UNIT", NEVER `"0.00"`.**
 */
export function amountIn(
  cell: RevenueProductTotalCell | undefined,
  currency: string,
): string | null {
  if (cell === undefined) return null;
  if (cell.total === null) return null;
  if (cell.totalCurrency !== currency) return null;
  return cell.total;
}

/**
 * A cycle's cell on a row.
 *
 * ⚠️ **Read through this rather than indexing `row.cycles` directly.** A cycle
 * in which a product sold nothing may arrive as a `{total: null}` cell or as a
 * missing key, and the two mean the same thing — `noUncheckedIndexedAccess`
 * makes the second a compile error at every other call site, which is a fine
 * way to be reminded and a poor way to have twelve of them.
 */
export function cellOf(
  row: RevenueProductRow,
  cycleName: string,
): RevenueProductTotalCell | undefined {
  return row.cycles[cycleName];
}

/**
 * How many rows carry a figure whose unit was never recorded.
 *
 * 🔴 **STATED ON SCREEN WHENEVER IT IS ABOVE ZERO.** These figures are real
 * money that is in none of the totals on this tab, so without this line the
 * report silently under-counts — the same class of omission `unmatchedLines`
 * exists to prevent, arriving by a different route.
 *
 * Counted per ROW rather than per cell: the realistic shape is a whole row
 * grouped under a null currency, and counting its eight cells would report
 * eight problems where there is one product to look at.
 *
 * ⚠️ **CONVERSION DOES NOT RETIRE THIS AND MAKES IT THE ONLY HONEST ACCOUNT OF
 * THAT POPULATION.** A line carrying no currency cannot be priced at any rate,
 * so it is withheld converted exactly as it was filtered unconverted — while
 * {@link excludedProductCount} beside it, which used to describe the same row in
 * currency-filter terms, is suppressed by the panel under conversion.
 */
export function unstatedUnitRowCount(rows: readonly RevenueProductRow[]): number {
  let count = 0;
  for (const row of rows) {
    const stated = (cell: RevenueProductTotalCell | undefined): boolean =>
      cell !== undefined && cell.total !== null && cell.totalCurrency === null;
    if (stated(row.rowTotal) || Object.values(row.cycles).some(stated)) count += 1;
  }
  return count;
}

/* -------------------------------------------------------------------------
 * The cycle scope
 * --------------------------------------------------------------------- */

/**
 * Which cycles the headline figures cover.
 *
 * ⚠️ **A UNION RATHER THAN AN ARRAY, BECAUSE THE TWO CASES USE DIFFERENT
 * ARITHMETIC.** Every cycle reads the server's own `rowTotal`; one cycle reads
 * that cycle's cell. Passing an array would invite re-summing the cells to get
 * the whole-scope figure — which means parsing decimal strings for a number the
 * server already computed exactly, for no gain and a rounding risk.
 */
export type CycleScope = { kind: 'all' } | { kind: 'one'; cycle: RevenueProductCycle };

/**
 * Resolves the chosen cycle against the data.
 *
 * ⚠️ **AN UNKNOWN CYCLE READS AS "EVERY CYCLE" RATHER THAN AS NONE.** The cycle
 * list is admin-maintained, so a saved link can name one that has since been
 * retired. An empty report reads as a business that traded nothing, which is
 * the opposite of what a stale filter means.
 */
export function scopeOf(
  cycles: readonly RevenueProductCycle[],
  cycleKey: string,
): CycleScope {
  if (cycleKey === ALL) return { kind: 'all' };
  const one = cycles.find((cycle) => cycle.name === cycleKey);
  return one === undefined ? { kind: 'all' } : { kind: 'one', cycle: one };
}

/** What one row contributed inside the scope, in the chosen currency. */
export function amountInScope(
  row: RevenueProductRow,
  scope: CycleScope,
  currency: string,
): string | null {
  return scope.kind === 'all'
    ? amountIn(row.rowTotal, currency)
    : amountIn(cellOf(row, scope.cycle.name), currency);
}

/* -------------------------------------------------------------------------
 * ⚠️ Naming a product, without folding three different absences into one
 * --------------------------------------------------------------------- */

/**
 * The product code the server gives revenue it could not attribute to any
 * catalogue item.
 *
 * 🔴 **THIS IS NOT A PRODUCT AND MUST NEVER BE PRESENTED AS ONE.** It is the
 * bucket for imported Xero lines whose item code matches nothing in
 * `d_products`, and on live data it **dominates the older cycles** — whole
 * historical years for some companies sit almost entirely under it.
 *
 * ⚠️ **IT IS SHOWN, NOT HIDDEN, AND THAT IS THE DECISION.** A "best sellers"
 * ranking that silently drops it lies about where the money came from; one that
 * silently lists it as a product lies about what was sold. So it is ranked with
 * everything else, badged on every row, and given a headline tile of its own
 * stating what share of the scope it is — a reader ranking products always sees
 * how much of the money is not attributed to one. A control offers to exclude
 * it, and the panel says so when it is excluded.
 *
 * ⚠️ **MATCHED ON `code`, WHICH IS THE LEAST BAD OF THREE POOR KEYS.** The id
 * (33 on production) is that database's own counter and means nothing
 * elsewhere; the name is editable wording. If the server ever renames the code,
 * this row reads as an ordinary product and the tile reads a suspicious `0%` —
 * visible, rather than silently wrong, which is the direction to fail in.
 */
export const UNCODED_PRODUCT_CODE = 'XERO-UNCODED';

/** The short mark on a row. */
export const UNCODED_BADGE = 'Not a catalogue product';

/**
 * What the uncatalogued revenue is, said once — both kinds together, for the
 * excluded-revenue notice.
 *
 * ⚠️ *SUPERSEDED 2026-10-15 (owner): only the Xero placeholder counted.* The
 * "No product recorded" row (lines naming no product at all) is uncatalogued
 * too — badged the same, in the Uncatalogued tile, and removed by the same
 * exclude toggle. See {@link isUncodedProduct}.
 */
export const UNCODED_MEANING =
  'Paid revenue not attributed to a catalogue product: imported Xero invoice lines whose item code matches nothing in the product catalogue, and invoices whose lines name no product. It is real revenue; it is not a product.';

/** The Xero placeholder row, said on its own badge. */
export const XERO_UNCODED_MEANING =
  'Imported Xero invoice lines whose item code matches nothing in the product catalogue. It is real revenue; it is not a product.';

/** The no-product row, said on its own badge. */
export const NO_PRODUCT_MEANING =
  'Invoice lines that name no product at all. It is real revenue; it is not a product.';

/**
 * WHICH of the three identifiers a label fell back to.
 *
 * ⚠️ **`product.id`, `product.name` AND `product.code` ARE INDEPENDENTLY
 * NULLABLE AND THE THREE NULLS MEAN DIFFERENT THINGS**, so
 * `name ?? code ?? id` is not a tidier label — it is one label silently
 * answering a different question per row. This says which, and every renderer
 * shows the difference.
 */
export type ProductLabelKind = 'named' | 'code-only' | 'id-only' | 'unidentified';

export interface ProductLabel {
  /** What to render. Never empty. */
  text: string;
  kind: ProductLabelKind;
  /** Why the label is not a name, or `null` when it is one. */
  note: string | null;
  /** The product code, when there is one, for rendering beside a name. */
  code: string | null;
}

/** `''` after trimming is an absent value, not a value. */
function present(value: string | null): string | null {
  if (value === null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function productLabelOf(product: RevenueProductRef | null): ProductLabel {
  const name = present(product?.name ?? null);
  const code = present(product?.code ?? null);
  const id = product?.id ?? null;

  if (name !== null) return { text: name, kind: 'named', note: null, code };
  if (code !== null) {
    return { text: code, kind: 'code-only', note: 'No product name recorded', code };
  }
  if (id !== null) {
    return {
      text: `Product #${id}`,
      kind: 'id-only',
      note: 'No name or code recorded',
      code: null,
    };
  }
  return {
    text: 'No product recorded',
    kind: 'unidentified',
    note: 'These lines resolved to no catalogue product at all',
    code: null,
  };
}

/**
 * The identity two rows are grouped by.
 *
 * ⚠️ **THE ID FIRST, THEN THE CODE, THEN THE NAME**, because that is their
 * order of stability — and each is PREFIXED, so a product with id `5` and one
 * with code `5` can never collide.
 *
 * ⚠️ **EVERY ROW IDENTIFYING NOTHING COLLAPSES INTO ONE GROUP**, deliberately:
 * they are all the same non-answer, and listing them separately would present a
 * set of unidentified lines as several distinct products.
 */
export function productKeyOf(product: RevenueProductRef | null): string {
  const id = product?.id ?? null;
  if (id !== null) return `id:${id}`;
  const code = present(product?.code ?? null);
  if (code !== null) return `code:${code}`;
  const name = present(product?.name ?? null);
  if (name !== null) return `name:${name}`;
  return 'unidentified';
}

/**
 * Whether a row is uncatalogued revenue rather than a product: the Xero
 * placeholder ({@link UNCODED_PRODUCT_CODE}), or a row identifying no product
 * at all ("No product recorded" — {@link productKeyOf}'s `unidentified`).
 * Owner decision 2026-10-15: both are badged, counted in the Uncatalogued
 * tile and removed by the exclude toggle alike.
 */
export function isUncodedProduct(product: RevenueProductRef | null): boolean {
  return (
    present(product?.code ?? null) === UNCODED_PRODUCT_CODE ||
    productKeyOf(product) === 'unidentified'
  );
}

/* -------------------------------------------------------------------------
 * 🔴 REGIONS OF FOCUS — the breakdown, and why it does not add up
 * --------------------------------------------------------------------- */

/**
 * 🔴 **THE EXPANDED ROW BREAKS DOWN BY REGION OF FOCUS, NOT BY BILLING REGION —
 * owner's instruction, 2026-09-13.**
 *
 * ⚠️ *SUPERSEDED: `ProductBandFigure` / `ProductGroup.bands`, one figure per
 * BILLING REGION, summing exactly to the row total.* **The billing region has not
 * gone anywhere** — it is still the row's band, still what {@link regionKeyOf}
 * and {@link regionOptionsOf} build the picker from, still what the Google Sheet
 * product tab is banded by. **Only the BREAKDOWN moved.** Do not read the
 * supersession as retiring the band; the two questions now live in two places on
 * purpose, and the bar's control narrows ROWS while this list decomposes one
 * product.
 *
 * ── 🔴 THIS IS NOT A DECOMPOSITION OF THE ROW TOTAL. IT SUMS HIGHER ─────────
 *
 * A project names a SET of regions, and the owner chose to **count revenue in
 * each region the project names, and say so** rather than to divide it between
 * them. So a project in three regions contributes its **whole** figure to all
 * three, and this list adds to three times that money.
 *
 * ⛔ **NEVER RENDER ONE OF THESE AS A PERCENTAGE OF THE ROW.** It can exceed
 * 100%, which reads as a rendering fault rather than as the arithmetic the owner
 * asked for — and a share that "should not" be above 100 is exactly the figure
 * somebody clamps, at which point the over-count becomes invisible.
 *
 * ⛔ **AND THE ROW TOTAL IS UNTOUCHED.** `rowTotal` is computed without the
 * fan-out and stays the money. Nothing here may be summed into it, compared as
 * its parts, or used to "correct" it.
 *
 * ── ⚠️ THREE KINDS, AND THE TWO ABSENCES ARE DIFFERENT FACTS ────────────────
 *
 * Merging them — with each other, or into a region — is the {@link
 * productLabelOf} mistake one level down: one label silently answering a
 * different question per entry. **One is somebody's job and one is the ordinary
 * state**, so they are worded apart rather than both reading as a gap.
 */
export type FocusRegionKind = 'region' | 'project-without-region' | 'no-project';

/**
 * One region-of-focus entry on a row, as the server sends it.
 *
 * ⚠️ **DECLARED HERE RATHER THAN IMPORTED, TEMPORARILY.** `RevenueProductRow` in
 * `@/lib/types` does not carry `focusRegions` yet — the branch that added this
 * breakdown could not edit that file. See {@link focusRegionsOf} for how it is
 * read until it does, and delete this declaration in favour of the import when
 * the key lands; nothing else has to change, because the two are structurally
 * identical.
 */
export interface RevenueProductFocusRegion {
  /** `focus:region:<id>` | `focus:project-without-region` | `focus:no-project`. */
  key: string;
  kind: FocusRegionKind;
  /**
   * The region, on a `region` entry.
   *
   * ⚠️ **`name` IS INDEPENDENTLY NULLABLE**, so a named region and a region row
   * carrying no name are a fourth absence — see {@link focusLabelOf}, which
   * falls back to the id and says that it did rather than rendering a blank.
   */
  region: { id: number; name: string | null } | null;
  /** Keyed by the cycle's NAME, exactly as `RevenueProductRow.cycles` is. */
  cycles: Record<string, RevenueProductTotalCell>;
  /** This entry's sum across every cycle. */
  total: RevenueProductTotalCell;
}

/**
 * How much of a row's revenue ran in SEVERAL regions of focus.
 *
 * 🔴 **`total` IS THE PRINCIPAL COUNTED ONCE. IT IS NOT THE EXCESS.** Sixty
 * pounds on a three-region project gives a principal of 60 and a breakdown of
 * 180. **The excess is derivable and the principal is not**, which is the whole
 * reason it is on the wire — a client that renders it as the excess understates
 * the gap by the region count, in the right currency, with nothing on screen
 * looking wrong.
 *
 * ⚠️ **DECLARED HERE TEMPORARILY, for the reason on {@link
 * RevenueProductFocusRegion}.**
 */
export interface RevenueProductSeveralFocusRegions {
  present: boolean;
  cycles: Record<string, RevenueProductTotalCell>;
  total: RevenueProductTotalCell;
}

/**
 * What each kind is, said once, for the `title` a label cannot hold.
 *
 * ⚠️ **A FACT ABOUT THE DATA, NOT HELPER TEXT** — the documented exception, and
 * routed to a `title` rather than printed beside every entry, which is what this
 * folder already does with `UNCODED_MEANING`.
 *
 * 🔴 **THE TWO ABSENCES SAY OPPOSITE THINGS ABOUT WHETHER ANYBODY SHOULD ACT.**
 * `project-without-region` is a project somebody can classify; `no-project` is
 * the ordinary state — `Meet Up`, `Profile Evaluations` and `Miscellaneous` are
 * never sold through a project and never will be, so wording it as a gap sends
 * a reader looking for data to fix that does not exist.
 */
export const FOCUS_MEANING: Record<FocusRegionKind, string> = {
  region:
    'The regions of focus named by the projects this revenue was sold against.',
  'project-without-region':
    'Sold against a project that names no region of focus. The project can be classified, and this figure moves to that region when it is.',
  'no-project':
    'Not sold through a project at all, so there is no region of focus to report. Ordinary for items like Meet Up, Profile Evaluations and Miscellaneous — there is nothing here to correct.',
};

/** What the reader calls each kind of absence. Written once so it cannot drift. */
export const NO_FOCUS_REGION_LABEL = 'Project has no region of focus';
export const NO_PROJECT_LABEL = 'Not sold through a project';

/**
 * What one entry is called, and why it is not a region's name.
 *
 * ⚠️ **FOUR ANSWERS, NOT THREE.** A `region` entry whose `name` is `null` falls
 * back to the id — the {@link productLabelOf} treatment — because a blank label
 * reads as a rendering bug and sends the reader hunting a broken join.
 */
export function focusLabelOf(entry: RevenueProductFocusRegion): string {
  if (entry.kind === 'project-without-region') return NO_FOCUS_REGION_LABEL;
  if (entry.kind === 'no-project') return NO_PROJECT_LABEL;
  const name = present(entry.region?.name ?? null);
  if (name !== null) return name;
  const id = entry.region?.id ?? null;
  return id === null ? 'Unnamed region of focus' : `Region #${id}`;
}

/**
 * The two keys, read off a row that may not carry them.
 *
 * ⚠️ **READ WITH A GUARD AND NEVER WITH A BARE CAST TO PRESENCE.** This app
 * deploys separately from the API, so a server that predates the breakdown is an
 * ordinary answer — and a missing array read with `.map` is a `TypeError` inside
 * a render, which React escalates to unmounting the tree. A blank dashboard
 * because one row was shaped unexpectedly is the failure this shape refuses.
 *
 * ⚠️ **`undefined` AND `[]` MEAN DIFFERENT THINGS, so the absence is carried
 * upward rather than folded here** — see {@link ProductGroup.focusReported}. A
 * server that reports nothing and a product genuinely in no region would
 * otherwise draw the same empty list, and the second is a claim about the data.
 */
type RowWithFocusRegions = RevenueProductRow & {
  focusRegions?: readonly RevenueProductFocusRegion[];
  revenueInSeveralFocusRegions?: RevenueProductSeveralFocusRegions;
};

function focusRegionsOf(
  row: RevenueProductRow,
): readonly RevenueProductFocusRegion[] | undefined {
  const carried = (row as RowWithFocusRegions).focusRegions;
  return Array.isArray(carried) ? carried : undefined;
}

function severalFocusRegionsOf(
  row: RevenueProductRow,
): RevenueProductSeveralFocusRegions | undefined {
  const carried = (row as RowWithFocusRegions).revenueInSeveralFocusRegions;
  return carried === null || typeof carried !== 'object' ? undefined : carried;
}

/**
 * A focus cell's figure inside the scope, in the chosen currency.
 *
 * ⚠️ **THE SAME CURRENCY GATE AS EVERY OTHER FIGURE ON THIS TAB** — {@link
 * amountIn}, on the cell's own `totalCurrency`. An unstated unit enters no sum
 * here either.
 *
 * ⚠️ **`cycles` IS GUARDED RATHER THAN INDEXED, for the reason stated at {@link
 * focusRegionsOf} one level up**: these two keys are not in `@/lib/types` yet, so
 * nothing type-checked the shape of what arrived. Indexing an absent `cycles` is
 * a `TypeError` inside a render, which unmounts the tree — a blank dashboard
 * because one entry was shaped unexpectedly. Arguing for the guard on the array
 * and then indexing its contents unguarded is the half-applied version of it.
 */
function focusAmountInScope(
  cells: { cycles: Record<string, RevenueProductTotalCell>; total: RevenueProductTotalCell },
  scope: CycleScope,
  currency: string,
): string | null {
  if (scope.kind === 'all') return amountIn(cells.total, currency);
  const cycles: unknown = cells.cycles;
  if (cycles === null || typeof cycles !== 'object') return null;
  return amountIn(
    (cycles as Record<string, RevenueProductTotalCell>)[scope.cycle.name],
    currency,
  );
}

/* -------------------------------------------------------------------------
 * One product, aggregated
 * --------------------------------------------------------------------- */

/**
 * What one product took in one region of focus, in the chosen currency.
 *
 * ⛔ **NOT A SHARE OF THE ROW, EVER.** See the section header: these sum higher
 * than the row total by design.
 */
export interface ProductFocusFigure {
  /** The server's own `key` — the React key and the aggregation identity. */
  key: string;
  kind: FocusRegionKind;
  /** What renders. Never empty. */
  label: string;
  /** What the entry IS, for the cell's `title`. */
  meaning: string;
  /** Exact, never `null` — an entry with nothing is absent rather than empty. */
  amount: string;
}

/**
 * How much of a product's revenue was counted under more than one region.
 *
 * 🔴 **`principal` IS THE MONEY, COUNTED ONCE — NOT THE EXCESS.** `breakdown`
 * and `rowTotal` are carried beside it so the sentence can state all three
 * rather than invite the reader to infer the relationship between them. See
 * {@link overCountSentence}, which is the one place they are worded.
 */
export interface FocusOverCount {
  /** From the wire. Never derived — the excess is derivable and this is not. */
  principal: string;
  /** The exact sum of {@link ProductGroup.focusRegions}. */
  breakdown: string;
  /** The row total, untouched by the fan-out. This is the money. */
  rowTotal: string;
}

/**
 * The one wording of the over-count.
 *
 * 🔴 **IT STATES ALL THREE FIGURES AND ASSERTS NO INEQUALITY BETWEEN THEM.**
 * *"adds to more than"* is a claim a currency-and-scope edge could falsify;
 * naming `breakdown` against `rowTotal` cannot be, and it is what a reader needs
 * anyway. The first figure is introduced as revenue that RAN in several regions,
 * never as a difference, because `principal` is not the difference.
 *
 * ⛔ **EXACT FIGURES, NEVER THE COMPACTED ONES.** This is a reconciliation
 * between three numbers, and `1.20M` against `1.20M` is a reconciliation that
 * silently agrees with itself — the failure this app already records against
 * compacting a gap figure. `formatDecimalString` plus the CODE, so the sentence
 * carries its own unit.
 *
 * ⚠️ **NOT HELPER TEXT.** Nothing else on the page can tell a reader that the
 * list below them adds to more than the row it is inside; it is the documented
 * fact-about-the-data exception, and a helper-text sweep must not take it.
 */
export function overCountSentence(over: FocusOverCount, currency: string): string {
  const money = (value: string): string => `${formatDecimalString(value)} ${currency}`;
  return (
    `${money(over.principal)} of this product's revenue ran in projects naming several ` +
    `regions of focus, and is counted in full under each of them. So the figures below ` +
    `add to ${money(over.breakdown)}, against the ${money(over.rowTotal)} this product ` +
    `actually took.`
  );
}

/** One cycle of a product's history, in the chosen currency. */
export interface ProductCycleFigure {
  cycle: RevenueProductCycle;
  /** `null` is "nothing in this currency in this cycle", never `0.00`. */
  amount: string | null;
}

export interface ProductGroup {
  /** {@link productKeyOf} — the React key and the grouping identity. */
  key: string;
  product: RevenueProductRef | null;
  label: ProductLabel;
  /** See {@link UNCODED_PRODUCT_CODE}. Shown, badged, and never silently dropped. */
  isUncoded: boolean;
  /**
   * The exact total across every BILLING BAND in scope, in the chosen currency.
   *
   * ⚠️ **NON-NULL BY CONSTRUCTION** — {@link groupProducts} drops a product with
   * nothing in scope rather than carrying it as an optional, so nothing
   * downstream has to re-decide what an absent figure means.
   *
   * 🔴 **THIS IS THE MONEY, AND {@link focusRegions} DOES NOT DECOMPOSE IT.**
   * Summed from the rows' own `rowTotal`/cycle cells, which the server computes
   * **without** the region-of-focus fan-out. Nothing in the focus breakdown may
   * be summed into it or checked against it.
   */
  total: string;
  /**
   * Every cycle, in date order, **regardless of the cycle scope**.
   *
   * 🔴 **NOT NARROWED BY THE SCOPE, DELIBERATELY.** It is the per-cycle history
   * the expanded row prints, and the answer to *is this product still selling*.
   * Narrowing it by the chosen cycle would leave one line in that list and the
   * history would be gone — the same rule the cycle strip follows.
   *
   * ⚠️ **`activeCycles` IS COUNTED OFF THIS**, so it is a count over every cycle
   * on record and not over the chosen scope. The column is headed `Cycles sold
   * in` for that reason.
   */
  byCycle: ProductCycleFigure[];
  /**
   * Which REGIONS OF FOCUS the money ran in, largest first.
   *
   * 🔴 **THESE SUM HIGHER THAN {@link total}, BY DESIGN** — see the
   * regions-of-focus section header. Never a share of the row, never summed into
   * the row, and never used to check it.
   *
   * ⚠️ **RANKED BY AMOUNT WITH THE TWO ABSENCES IN THE RANKING, not pushed to the
   * bottom.** Same decision the uncatalogued bucket gets: `no-project` can be
   * most of a product's money, and filing the largest figure last so that it
   * looks tidy is a ranking that lies about where the revenue came from.
   */
  focusRegions: ProductFocusFigure[];
  /**
   * Whether the server reported a focus breakdown at all.
   *
   * 🔴 **`false` IS "THE SERVER DID NOT SAY", AND AN EMPTY LIST IS "IN NO
   * REGION".** They draw the same nothing, and only the second is a fact about
   * this product — so the renderer must word them apart. `false` on a server that
   * predates the breakdown is an ordinary answer, not a fault.
   */
  focusReported: boolean;
  /**
   * How much of {@link total} was counted under more than one region.
   *
   * 🔴 **`null` IS "NOTHING OVER-COUNTED" AND NEVER A ZERO.** It is `null`
   * whenever no row declared `present`, or the principal carries no figure in
   * this currency and scope — and a `0.00` printed there would claim the
   * breakdown reconciles when the question was never answered.
   */
  overCount: FocusOverCount | null;
  /**
   * The OTHER currencies this product was also sold in.
   *
   * 🔴 **EMPTY ON EVERY CONVERTED RESPONSE, WHICH IS EVERY ORDINARY RESPONSE
   * SINCE 2026-09-14 — SO NOTHING RENDERS IT AND NOTHING SHOULD.** ⚠️
   * *SUPERSEDED, and it was true for as long as the currency was a filter: "**THE
   * ONLY THING THAT CAN SAY THE FIGURE BESIDE IT IS PARTIAL.** A product sold in
   * two currencies is on this list twice, once under each, and nothing converts
   * between them."* Converted, the figure beside it **is** what the product
   * earns: the sibling rows it pointed at have been summed into this one, so a
   * badge reading *"Also sold in USD"* would send a reader looking for a second
   * row that does not exist and would call a complete figure partial. Measured
   * before it was withdrawn: it applied to **19 of 28 products**, so this is a
   * mark disappearing from most of the table rather than an edge case.
   *
   * ⛔ **THE FIELD IS KEPT AND GATED RATHER THAN DELETED, and the distinction
   * matters.** An UNCONVERTED response — a server predating `baseCurrency`, or
   * any request that sent none — is still two rows in two currencies that are
   * still never added, and there the badge is still the only honest mark on a
   * partial figure. It is gated on {@link groupProducts}' `converted`, which
   * reads the server's ECHO: withdrawing the mark on the strength of what was
   * REQUESTED would take it away on exactly the responses that still need it.
   *
   * ⚠️ **`RevenueProductTable` renders it on `.length > 0` and needs no change**
   * — the empty array is what removes it from the page, so there is no second
   * place for this decision to drift to.
   */
  alsoSoldIn: string[];
  /** How many cycles carry a figure. At least 1. */
  activeCycles: number;
  /**
   * The product's money split by COMPANY, one entry per (company, cycle) in
   * scope, for the companies × cycles matrix. See {@link companyLinesOf}.
   *
   * 🔴 **`null` IS "THE SERVER DID NOT SAY"** — an API predating
   * `RevenueProductRow.companies` — and the matrix is withheld on it. `[]` is a
   * real answer (no company carries a figure in scope).
   */
  companyLines: CompanyMatrixLine[] | null;
  /**
   * The product's revenue in its original currencies, one figure per currency,
   * across every cycle. See {@link nativeTotalsOf}. `null` when the server did
   * not say (an older API) — the line is then hidden.
   */
  nativeTotals: NativeTotal[] | null;
}

/** One currency's unconverted figure. */
export interface NativeTotal {
  currency: string;
  amount: string;
}

/**
 * A product's native-currency totals, summed across its billing-band rows
 * WITHIN each currency only — two figures in one currency add exactly; two
 * currencies are never added. Ordered by currency code (no float ordering).
 *
 * ⚠️ **`null` WHEN ANY ROW OF THE GROUP LACKS `nativeTotals`.** A partial list
 * would read as the whole product. A blank currency code is dropped rather
 * than labelled — an unstated unit is not a figure in any currency.
 */
export function nativeTotalsOf(groupRows: readonly RevenueProductRow[]): NativeTotal[] | null {
  const byCurrency = new Map<string, string[]>();
  for (const row of groupRows) {
    if (row.nativeTotals === undefined) return null;
    for (const entry of row.nativeTotals) {
      const code = present(entry.currency);
      if (code === null) continue;
      const amounts = byCurrency.get(code) ?? [];
      amounts.push(entry.amount);
      byCurrency.set(code, amounts);
    }
  }
  const totals: NativeTotal[] = [];
  for (const [currency, amounts] of byCurrency) {
    const amount = sumDecimalStrings(amounts);
    if (amount !== null) totals.push({ currency, amount });
  }
  return totals.sort((a, b) => a.currency.localeCompare(b.currency));
}

/** The "facts about the data" line: `USD 717,975.00 · EUR 214,500.00`. */
export function nativeTotalsText(totals: readonly NativeTotal[]): string {
  return totals
    .map((entry) => `${entry.currency} ${formatDecimalString(entry.amount)}`)
    .join(' · ');
}

/**
 * One product's lines in the shape `buildProductCompanyMatrix` takes.
 *
 * 🔴 **THE SAME CELLS, THROUGH THE SAME GATE, AS {@link ProductGroup.total}.**
 * Each company cell passes {@link amountIn} against the unit the figures are in,
 * over exactly the cycles in scope — every cycle under "All cycles", the chosen
 * one otherwise — and only over the rows that contributed to the group. So the
 * matrix's grand total is the product row's figure whenever the server's
 * company split sums to its own row (the table checks that in development).
 *
 * ⚠️ **`null` WHEN ANY ROW OF THE GROUP LACKS `companies`.** A partial split
 * would be a matrix short by a band, presented as the whole product.
 *
 * ⚠️ **The company's band is the ROW's band** — a row is one billing band, and
 * every company under it is in that band. It is passed as the band's NAME (the
 * matrix shows it), with {@link NO_REGION_LABEL} for the unbanded companies,
 * which are a real population rather than an unknown.
 */
export function companyLinesOf(
  groupRows: readonly RevenueProductRow[],
  scopeCycles: readonly RevenueProductCycle[],
  currency: string,
): CompanyMatrixLine[] | null {
  const lines: CompanyMatrixLine[] = [];
  for (const row of groupRows) {
    if (row.companies === undefined) return null;
    const billingRegion = regionLabelOf(row);
    for (const entry of row.companies) {
      for (const cycle of scopeCycles) {
        const amount = amountIn(entry.cycles[cycle.name], currency);
        if (amount === null) continue;
        lines.push({
          cycleId: String(cycle.id),
          companyId: String(entry.company.id),
          companyName: entry.company.name,
          billingRegion,
          amount,
        });
      }
    }
  }
  return lines;
}

/** The cycles a scope covers, in date order: every cycle, or the chosen one. */
export function scopeCyclesOf(
  cycles: readonly RevenueProductCycle[],
  scope: CycleScope,
): readonly RevenueProductCycle[] {
  return scope.kind === 'all' ? cycles : [scope.cycle];
}

/** The matrix's columns, from the cycles in scope (already in date order). */
export function matrixCyclesOf(cycles: readonly RevenueProductCycle[]): MatrixCycle[] {
  return cycles.map((cycle) => ({ id: String(cycle.id), name: cycle.name, start: cycle.startDate }));
}

/**
 * The cycle containing today, strictly — `null` when today falls in no window.
 * It is the matrix's "current" column bound; a cycle that does not contain today
 * is never marked as current.
 */
export function todaysCycleIdOf(cycles: readonly RevenueProductCycle[]): number | null {
  return cycleContaining([...cycles], todayIso())?.id ?? null;
}

/* -------------------------------------------------------------------------
 * How an expanded product is shown
 * --------------------------------------------------------------------- */

/**
 * The companies × cycles matrix (`table`), or the cycle and region-of-focus
 * breakdown (`breakdown`).
 */
export type ProductExpandView = 'table' | 'breakdown';

/**
 * Where the reader's choice is remembered, per browser.
 *
 * ⚠️ **THE STRING IS THE STORAGE KEY**; renaming it drops every saved choice.
 */
export const PRODUCT_EXPAND_VIEW_KEY = 'emerge.revenueProduct.expandView';

/** The stored choice, or the default (`table`). Storage can throw; it is a convenience only. */
export function readProductExpandView(): ProductExpandView {
  try {
    return window.localStorage.getItem(PRODUCT_EXPAND_VIEW_KEY) === 'breakdown'
      ? 'breakdown'
      : 'table';
  } catch {
    return 'table';
  }
}

export function writeProductExpandView(view: ProductExpandView): void {
  try {
    window.localStorage.setItem(PRODUCT_EXPAND_VIEW_KEY, view);
  } catch {
    /* Private window or blocked storage: the choice lasts this page only. */
  }
}

/** The id inside a company matrix row's key (`company:<id>`), or `null`. */
export function companyIdOfMatrixKey(key: string): string | null {
  const match = /^company:(\d+)$/.exec(key);
  return match?.[1] ?? null;
}

/**
 * Every product with revenue in this unit and this scope, ranked largest first.
 *
 * ⚠️ **BANDS ARE ADDED AND UNITS ARE NOT.** Two rows for one product in two
 * billing regions are two figures in the SAME unit, so their sum is exact and
 * correct. Two figures in two different units are never added — {@link amountIn}
 * has already withheld everything that is not `currency`. That is unchanged by
 * conversion: converted, the server has restated every currency INTO the unit
 * before this function sees it, so the summing here is the same code over a
 * wider population rather than a rule being relaxed.
 *
 * 🔴 **AND THE REGION-OF-FOCUS BREAKDOWN IS ADDED ACROSS ROWS TOO — BUT IT IS
 * NOT A DECOMPOSITION OF `total`.** Each row carries its own `focusRegions`, and
 * the same region appearing on two of a product's rows is two figures in one
 * unit. What is **not** true is that the result sums to the row total: a project
 * naming several regions is counted in full under each. See the regions-of-focus
 * section header, and {@link ProductGroup.overCount}, which is the only thing on
 * screen that can say so.
 *
 * ⚠️ **THE RANK IS TAKEN ON A `Number`, AND THE FIGURE IS NOT.** Ordering needs
 * a comparison and nothing else. Sorting the strings themselves would put
 * `"9.00"` above `"10000.00"` lexicographically — a ranked table that is simply
 * wrong with nothing on screen saying so. The tie-break is the label, so two
 * products on an identical figure do not swap places between renders.
 *
 * @param currency the unit the figures are in — the server's ECHOED base where
 *   it converted, the picker's value where it did not. ⛔ Never the string that
 *   was sent; see {@link amountIn}.
 * @param converted whether the SERVER said it converted, i.e. whether
 *   `meta.measuredInCurrency` came back. It decides exactly one thing:
 *   whether {@link ProductGroup.alsoSoldIn} is populated. ⛔ **Read it off the
 *   echo, never off whether a base was requested** — an older API ignores the
 *   key and answers unconverted rows, which is the one response that still needs
 *   the mark.
 */
export function groupProducts(
  rows: readonly RevenueProductRow[],
  cycles: readonly RevenueProductCycle[],
  scope: CycleScope,
  currency: string,
  converted: boolean,
): ProductGroup[] {
  /*
   * ⚠️ Built over EVERY row before the unit gate, because its whole job is to
   * report the currencies the gate is hiding — and on a CONVERTED response the
   * gate is hiding none of them, so it is not built at all.
   *
   * ⛔ **THE EARLY EXIT IS THE WHOLE OF HOW THE "also sold in" MARK IS
   * WITHDRAWN.** Populating it and leaving the renderer to ignore it is the
   * arrangement this file already refuses elsewhere: a marker that can no longer
   * be true, sitting in the data, one `.length > 0` away from being believed.
   */
  const currenciesByProduct = new Map<string, Set<string>>();
  if (!converted) {
    for (const row of rows) {
      const code = present(row.currency);
      if (code === null) continue;
      const key = productKeyOf(row.product);
      const seen = currenciesByProduct.get(key) ?? new Set<string>();
      seen.add(code);
      currenciesByProduct.set(key, seen);
    }
  }

  /* Every row of a product, scope or no scope: the native totals cover every
   * cycle, so a band row with nothing in the chosen cycle still counts there. */
  const everyRowByProduct = new Map<string, RevenueProductRow[]>();
  for (const row of rows) {
    const key = productKeyOf(row.product);
    const bucket = everyRowByProduct.get(key);
    if (bucket === undefined) everyRowByProduct.set(key, [row]);
    else bucket.push(row);
  }

  const collected = new Map<string, RevenueProductRow[]>();
  for (const row of rows) {
    if (amountInScope(row, scope, currency) === null) continue;
    const key = productKeyOf(row.product);
    const bucket = collected.get(key);
    if (bucket === undefined) collected.set(key, [row]);
    else bucket.push(row);
  }

  const groups: ProductGroup[] = [];

  for (const [key, groupRows] of collected) {
    const total = sumDecimalStrings(
      groupRows.map((row) => amountInScope(row, scope, currency)),
    );
    /* Unreachable — every row here contributed a figure — but the type says so
     * and a guard is cheaper than an assertion that could one day be wrong. */
    if (total === null) continue;

    const byCycle: ProductCycleFigure[] = cycles.map((cycle) => ({
      cycle,
      amount: sumDecimalStrings(
        groupRows.map((row) => amountIn(cellOf(row, cycle.name), currency)),
      ),
    }));

    /*
     * 🔴 **AGGREGATED ACROSS THE GROUP'S ROWS BY THE SERVER'S OWN `key`.** One
     * product can hold several rows — one per billing region — and each carries
     * its own focus breakdown, so the same region of focus legitimately appears
     * on two of them and the two figures are in one unit and are added.
     *
     * ⚠️ **THE KIND AND THE LABEL COME FROM THE FIRST ENTRY UNDER A KEY AND
     * CANNOT DISAGREE BETWEEN ROWS** — the key encodes the kind and the region
     * id, so two entries sharing a key are the same bucket by construction.
     */
    const focusTotals = new Map<
      string,
      { kind: FocusRegionKind; label: string; amounts: (string | null)[] }
    >();
    /* `undefined` on every row means the server said nothing; `[]` on a row is a
     * product genuinely in no region. See `focusReported`. */
    let focusReported = false;
    for (const row of groupRows) {
      const entries = focusRegionsOf(row);
      if (entries === undefined) continue;
      focusReported = true;
      for (const entry of entries) {
        const bucket = focusTotals.get(entry.key) ?? {
          kind: entry.kind,
          label: focusLabelOf(entry),
          amounts: [],
        };
        bucket.amounts.push(focusAmountInScope(entry, scope, currency));
        focusTotals.set(entry.key, bucket);
      }
    }

    const focusRegions: ProductFocusFigure[] = [];
    for (const [focusKey, entry] of focusTotals) {
      const amount = sumDecimalStrings(entry.amounts);
      if (amount === null) continue;
      focusRegions.push({
        key: focusKey,
        kind: entry.kind,
        label: entry.label,
        meaning: FOCUS_MEANING[entry.kind],
        amount,
      });
    }
    /* Largest first, tie-broken on the label so two equal figures cannot swap
     * places between renders — a list that reorders under the reader is
     * indistinguishable from data changing. */
    focusRegions.sort(
      (a, b) => Number(b.amount) - Number(a.amount) || a.label.localeCompare(b.label),
    );

    /*
     * 🔴 **BOTH HALVES MUST AGREE BEFORE A SENTENCE IS SHOWN: the row has to
     * DECLARE `present`, and the principal has to carry a figure in this currency
     * and scope.** `total: null` means nothing over-counted (so there is nothing
     * to say), and `present: false` beside a figure is a server contradicting
     * itself — withholding is the safe direction, because the alternative is a
     * notice claiming an over-count nobody reported.
     */
    const overCountPrincipal = sumDecimalStrings(
      groupRows.map((row) => {
        const several = severalFocusRegionsOf(row);
        if (several === undefined || several.present !== true) return null;
        return focusAmountInScope(several, scope, currency);
      }),
    );
    const focusBreakdown = sumDecimalStrings(focusRegions.map((entry) => entry.amount));
    const overCount: FocusOverCount | null =
      overCountPrincipal === null || focusBreakdown === null
        ? null
        : { principal: overCountPrincipal, breakdown: focusBreakdown, rowTotal: total };

    const first = groupRows[0];
    const product = first?.product ?? null;

    groups.push({
      key,
      product,
      label: productLabelOf(product),
      isUncoded: isUncodedProduct(product),
      total,
      byCycle,
      focusRegions,
      focusReported,
      overCount,
      alsoSoldIn: [...(currenciesByProduct.get(key) ?? [])]
        .filter((code) => code !== currency)
        .sort(),
      activeCycles: byCycle.filter((entry) => entry.amount !== null).length,
      companyLines: companyLinesOf(groupRows, scopeCyclesOf(cycles, scope), currency),
      nativeTotals: nativeTotalsOf(everyRowByProduct.get(key) ?? groupRows),
    });
  }

  return groups.sort((a, b) => {
    const delta = Number(b.total) - Number(a.total);
    if (delta !== 0) return delta;
    return a.label.text.localeCompare(b.label.text);
  });
}

/** The exact sum of a set of products. `null` when the set is empty. */
export function totalOf(groups: readonly ProductGroup[]): string | null {
  return sumDecimalStrings(groups.map((group) => group.total));
}

/**
 * How many products took NO revenue at all in the chosen unit.
 *
 * 🔴 **AN UNCONVERTED-ONLY MEASUREMENT SINCE 2026-09-14, AND ITS CALLER MUST
 * SUPPRESS IT UNDER CONVERSION.** It exists because the currency control used to
 * HIDE PRODUCTS and nothing else reported it — measured on production: 652 USD
 * invoices against 89 EUR and 56 GBP, so a non-USD view legitimately dropped most
 * of the catalogue, and a shorter list is indistinguishable from a catalogue
 * nobody is selling. **Converted, the control hides nothing**: every product with
 * any paid revenue has revenue in the base, so this reads `0` of its own accord
 * on a healthy response and the notice never draws.
 *
 * ⛔ **BUT IT DOES NOT READ `0` FOR CERTAIN, AND THAT IS WHY THE PANEL PASSES
 * `0` EXPLICITLY RATHER THAN TRUSTING IT TO.** A product whose only figure
 * carries an UNSTATED unit is admitted by no gate in any mode, so it would be
 * counted here and reported as *"took no GBP … they may have sold in another
 * currency"* — a sentence naming a policy that no longer exists, over a product
 * whose real problem is an invoice with no currency on it.
 * {@link unstatedUnitRowCount} reports that population honestly, and two notices
 * describing one row in incompatible terms is worse than one.
 *
 * ⚠️ **TAKEN OVER EVERY CYCLE, NOT THE SCOPE**, deliberately: it answers *"what
 * is missing because of the CURRENCY"*. Narrowing it by cycle would fold in
 * products absent for an ordinary reason — nobody bought them that year — and
 * turn a fact about a control into a fact about the business.
 */
export function excludedProductCount(
  rows: readonly RevenueProductRow[],
  currency: string,
): number {
  const withRevenue = new Set<string>();
  const all = new Set<string>();
  for (const row of rows) {
    const key = productKeyOf(row.product);
    all.add(key);
    if (amountIn(row.rowTotal, currency) !== null) withRevenue.add(key);
  }
  return all.size - withRevenue.size;
}

/* -------------------------------------------------------------------------
 * The cycle strip
 * --------------------------------------------------------------------- */

/** One cycle's figure for the products currently in play. */
export interface CycleTotal {
  cycle: RevenueProductCycle;
  amount: string | null;
}

/**
 * What each cycle contributed.
 *
 * 🔴 **TAKEN OVER EVERY CYCLE AND NEVER NARROWED BY THE CYCLE SELECTION.** The
 * strip is also the cycle PICKER, so narrowing it by its own selection would
 * leave one bar on screen — the reader would lose the comparison by touching
 * the control that exists to move through it.
 */
export function cycleTotalsOf(
  rows: readonly RevenueProductRow[],
  cycles: readonly RevenueProductCycle[],
  currency: string,
): CycleTotal[] {
  return cycles.map((cycle) => ({
    cycle,
    amount: sumDecimalStrings(
      rows.map((row) => amountIn(cellOf(row, cycle.name), currency)),
    ),
  }));
}

/* -------------------------------------------------------------------------
 * Shares
 * --------------------------------------------------------------------- */

/**
 * `part / whole` as a percentage, or `null` where no percentage exists.
 *
 * ⚠️ **`null` IN THREE REAL CASES, AND NONE OF THEM IS `0%`**: no total at all,
 * a total of exactly zero (dividing by it is an infinity), and a malformed
 * figure. Each renders as an em dash — printing `0%` states a share that was
 * never computed.
 *
 * ⚠️ **NOT CLAMPED.** Only a BAR's geometry is clamped, in the component that
 * draws it; clamping a figure because it "should not" happen is how a real
 * defect becomes invisible.
 *
 * ⚠️ **`Number()` HERE IS THE SANCTIONED EXCEPTION** — a ratio is not money.
 */
export function shareOf(part: string, whole: string | null): number | null {
  if (whole === null) return null;
  const partNum = Number(part);
  const wholeNum = Number(whole);
  if (!Number.isFinite(partNum) || !Number.isFinite(wholeNum) || wholeNum === 0) return null;
  return (partNum / wholeNum) * 100;
}

/** A share as text, to one decimal place. An em dash for `null`, never `0%`. */
export function shareText(share: number | null): string {
  if (share === null || !Number.isFinite(share)) return '—';
  return `${Math.round(share * 10) / 10}%`;
}

/* -------------------------------------------------------------------------
 * Ordering, from the column headers
 * --------------------------------------------------------------------- */

/**
 * The columns the table can be ordered by.
 *
 * 🔴 **A COLUMN MAY ONLY DECLARE A `sortKey` THAT IS ONE OF THESE, AND THE
 * COMPILER IS WHAT ENFORCES IT.** `ColumnSpec.sortKey` is a bare `string`, so
 * a typo there is accepted, the funnel renders its Ascending/Descending pair,
 * every click updates `useTableFilters`' sort state — and {@link
 * sortProductGroups} does not recognise the key, so **nothing reorders and
 * nothing errors**. That is the exact defect `sortChoices` was added to
 * `CompaniesPage` to make unconstructible, arriving from the other end.
 *
 * The panel declares each sortable column through a helper typed on
 * {@link ProductSortKey}, so an unbacked key is a BUILD failure rather than a
 * control that refuses every click.
 */
export const PRODUCT_SORT_KEYS = ['name', 'cycles', 'revenue'] as const;

export type ProductSortKey = (typeof PRODUCT_SORT_KEYS)[number];

export interface ProductOrder {
  key: ProductSortKey;
  direction: 'asc' | 'desc';
}

/**
 * `useTableFilters` hands back `{ key: string; direction }` — its `sortKey` is
 * typed for an API that sorts, and this endpoint does not. Narrowed here so
 * the caller has to handle an unknown key rather than fall through to a
 * default that looks like a working sort.
 */
export function asProductOrder(
  sort: { key: string; direction: 'asc' | 'desc' } | null,
): ProductOrder | null {
  if (sort === null) return null;
  return (PRODUCT_SORT_KEYS as readonly string[]).includes(sort.key)
    ? { key: sort.key as ProductSortKey, direction: sort.direction }
    : null;
}

/**
 * The products, ordered.
 *
 * 🔴 **THIS SORTS ROWS IN THE BROWSER, WHICH `useTableFilters` DELIBERATELY
 * REFUSES TO DO — AND THE REASON IT REFUSES DOES NOT HOLD HERE.** That hook's
 * header says it never sorts rows itself because "reordering one page while
 * implying the whole table is ordered is worse than no sort at all", and every
 * one of its fourteen consumers therefore feeds `sort` to a server request.
 *
 * ⚠️ **`GET /revenue-tracker/products` HAS NO PAGER AND `sort` IS A 422.** The
 * row set is bounded by the catalogue rather than by the data, so ONE PAGE IS
 * THE WHOLE SET — there is no truncated remainder for a local sort to lie
 * about. Sorting here orders every row there is.
 *
 * ⚠️ **IF THIS ENDPOINT EVER GAINS A PAGER, THIS FUNCTION BECOMES THE LIE THE
 * HOOK WARNS ABOUT** — silently, because a sorted page of a paged set looks
 * exactly like a sorted table. The pager is the thing to check, not this code.
 *
 * `null` is the opening order: largest paid revenue first, which is the
 * ranking the tab exists to show.
 */
export function sortProductGroups(
  groups: readonly ProductGroup[],
  order: ProductOrder | null,
): ProductGroup[] {
  const ranked = [...groups];

  /* The tie-break is always the label, so rows cannot swap places between
   * renders — a ranked table that reorders under the reader is
   * indistinguishable from data changing. */
  const byName = (a: ProductGroup, b: ProductGroup): number =>
    a.label.text.localeCompare(b.label.text);

  if (order === null) {
    return ranked.sort((a, b) => Number(b.total) - Number(a.total) || byName(a, b));
  }

  /* One place decides which way round a direction reads, so "Ascending" cannot
   * mean smallest-first on one column and A–Z-last on another. */
  const flip = order.direction === 'asc' ? 1 : -1;

  switch (order.key) {
    case 'revenue':
      return ranked.sort(
        (a, b) => flip * (Number(a.total) - Number(b.total)) || byName(a, b),
      );
    case 'cycles':
      return ranked.sort((a, b) => flip * (a.activeCycles - b.activeCycles) || byName(a, b));
    case 'name':
      return ranked.sort((a, b) => flip * byName(a, b));
    default: {
      const unreachable: never = order.key;
      return unreachable;
    }
  }
}

/* -------------------------------------------------------------------------
 * Tax measure
 * --------------------------------------------------------------------- */

/**
 * The measure this tab ASKS for: cash collected including tax (owner decision
 * 2026-10-16). Sent as `measure`; see {@link measureOfResponse} for what is
 * labelled.
 */
export const REQUESTED_TAX_MEASURE: RevenueTaxMeasure = 'inclTax';

/**
 * The measure to LABEL the figures with, from the server's `meta.measure`
 * echo.
 *
 * 🔴 **A PRESENT ECHO THAT IS NOT `inclTax` IS LABELLED `exTax`**, so the label
 * never claims tax that the figures do not contain. An absent echo is taken as
 * the request having been honoured (the backend contract ships the echo with
 * the parameter).
 */
export function measureOfResponse(echo: RevenueTaxMeasure | undefined): RevenueTaxMeasure {
  if (echo === undefined) return REQUESTED_TAX_MEASURE;
  return echo === 'inclTax' ? 'inclTax' : 'exTax';
}

/** The words every label on this tab uses for a measure. */
export function taxMeasureLabel(measure: RevenueTaxMeasure): string {
  return measure === 'inclTax' ? 'incl. tax' : 'ex-tax';
}
